// File input field - mode-aware
// Edit/Create: circular avatar with upload button
// View/Admin-view: displays image preview or placeholder
import { useRef, useState, useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { FieldError, FieldWrapper } from '@startus/ui';
import { useFormMode } from '../../hooks/useFormMode';
import uploadIcon from '../../assets/icons/upload-icon.svg';
import type { BaseFieldProps } from '../../types/fields';

export function FileField({ name, label, helpText }: BaseFieldProps) {
  const { isReadOnly } = useFormMode();
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();

  const value = watch(name);
  const error = errors[name]?.message as string | undefined;

  // Generate preview URL from File or string
  useEffect(() => {
    if (!value) {
      setPreview(null);
      return;
    }
    if (value instanceof File) {
      const url = URL.createObjectURL(value);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    if (typeof value === 'string') {
      // BE returns value as a stringified array e.g. "[\"https://...\"]"
      try {
        const parsed = JSON.parse(value);
        setPreview(Array.isArray(parsed) ? parsed[0] : value);
      } catch {
        setPreview(value);
      }
    }
  }, [value]);

  // Read-only: show preview or placeholder circle
  if (isReadOnly) {
    return (
      <FieldWrapper>
        {/*<Label required={required} fieldNumber={fieldNumber}>{label}</Label>*/}
        <div className="w-32 h-32 rounded-full bg-base-300 overflow-hidden flex items-center justify-center">
          {preview ? (
            <img src={preview} alt={label} className="w-full h-full object-cover" />
          ) : null}
        </div>
        {helpText && <p className="text-sm text-base-content/60 mt-1">{helpText}</p>}
      </FieldWrapper>
    );
  }

  // Editable: circular preview with upload button overlay
  return (
    <FieldWrapper>
      {/*<Label required={required} fieldNumber={fieldNumber}>{label}</Label>*/}
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, onBlur } }) => (
          <div className="w-full justify-center flex">
            <div className="relative w-32 h-32">
              {/* Circular preview area */}
              <div className="w-32 h-32 rounded-full bg-base-300 overflow-hidden">
                {preview ? (
                  <img src={preview} alt={label} className="w-full h-full object-cover" />
                ) : null}
              </div>
              {/* Upload button positioned at bottom-right */}
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="absolute bottom-1 right-1 btn btn-primary btn-sm px-2"
              >
                <img src={uploadIcon} alt="Upload" className="w-4 h-4" />
              </button>
              {/* Hidden file input */}
              <input
                type="file"
                accept="image/*"
                ref={inputRef}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  onChange(file);
                }}
                onBlur={onBlur}
              />
            </div>
          </div>
        )}
      />
      {helpText && <p className="text-sm text-base-content/60 mt-1">{helpText}</p>}
      <FieldError message={error} />
    </FieldWrapper>
  );
}
